import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type {
  GlobalCreditAccountTransferSourceIds,
  TransferGlobalCreditModalStore,
} from "@src/components/modules/credit-account/global-account-detail/components/transfer-credit-modal/store";
import { InputDatePicker, NumberInput, Select } from "@src/components/ui-kit";
import { GlobalCreditAccountSelect } from "@src/components/ui-kit/global-credit-account-select";
import { ProjectSelect } from "@src/widgets/ProjectSelect/ProjectSelect";
import { observer } from "mobx-react-lite";

interface Props {
  ids: GlobalCreditAccountTransferSourceIds;
  onSubmit: () => void;
  modalTitle?: string;
  ignoreIds?: string[];
  store: TransferGlobalCreditModalStore;
  enableSourceSelect?: boolean;
}
export const TransferGlobalCreditModal = observer(
  function TransferGlobalCreditModal({
    onSubmit,
    ids,
    modalTitle = t`Get credit`,
    ignoreIds,
    store,
    enableSourceSelect,
  }: Props) {
    return (
      <Modal
        isCentered
        isOpen={store.getCreditModalDisclosure.isOpen}
        onClose={store.getCreditModalDisclosure.onClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>{modalTitle}</ModalHeader>
          <ModalBody>
            {enableSourceSelect && (
              <Box mb="2">
                <Select
                  label={t`Transfer location`}
                  placeholder={t`Select transfer location`}
                  onChange={store.creditForm.$.transfer_location.onChange}
                  value={store.creditForm.$.transfer_location.value}
                  options={store.transferLocationOptions}
                />
              </Box>
            )}

            {store.transferToCreditAccount ? (
              <GlobalCreditAccountSelect
                options={[]}
                label={t`Credit account`}
                placeholder={t`Select credit account`}
                value={store.creditForm.$.global_account_id.value}
                onChange={store.handleSelectCreditAccount}
                error={store.creditForm.$.global_account_id.error}
                ignoreIds={ignoreIds}
              />
            ) : (
              <>
                <Box mb="2">
                  <ProjectSelect
                    options={[]}
                    label={t`Project`}
                    placeholder={t`Select project`}
                    value={store.creditForm.$.project_id.value}
                    onChange={store.handleSelectProject}
                  />
                </Box>
                <Select
                  options={store.budgetItemOptions}
                  isLoading={store.isBudgetItemOptionsLoading}
                  noOptionsMessage={() =>
                    store.creditForm.$.project_id.value
                      ? t`No budget items found`
                      : t`Select project first`
                  }
                  label={t`Expense budget item`}
                  placeholder={t`Select expense budget item`}
                  value={store.creditForm.$.to_account_id.value}
                  onChange={store.creditForm.$.to_account_id.onChange}
                />
              </>
            )}

            <Box my="2">
              <NumberInput
                currency
                label={t`Credit amount`}
                placeholder={t`Credit amount`}
                value={store.creditForm.$.amount.value}
                onChange={store.creditForm.$.amount.onChange}
                error={store.creditForm.$.amount.error}
              />
            </Box>

            <InputDatePicker
              label={t`Transaction date`}
              selected={store.creditForm.$.date.value}
              onChange={(range) => {
                store.creditForm.$.date.onChange(range?.start);
              }}
              error={store.creditForm.$.date.error}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="grey"
              onClick={store.getCreditModalDisclosure.onClose}
              variant="outline"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              ml="2"
              isLoading={store.isCreditTransferLoading}
              onClick={() => {
                store.handleTransferCredit(ids, onSubmit);
              }}
            >
              {modalTitle}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  },
);
